import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { initialDashboardPageMetadata, mergeDshbPageMetadataObjects } from "../../utils";
import * as api from "./api";

// Wraps an API call as a thunk whose rejection carries { status, code, message, fields }.
const thunk = (type, call, options) =>
  createAsyncThunk(
    `ads/${type}`,
    async (payload, { rejectWithValue, dispatch, getState }) => {
      try {
        return await call(payload, { dispatch, state: getState().ads });
      } catch (error) {
        return rejectWithValue(api.toRejection(error));
      }
    },
    options
  );

// `silent: true` is a background reload: it leaves `loading` and the form error alone.
export const readCampaignsAsync = thunk(
  "campaigns/readAll",
  ({ silent, ...payload } = {}, { state }) => api.listCampaigns(payload, state.campaigns.metadata)
);
export const readCampaignAsync = thunk("campaigns/read", ({ id }) => api.readCampaign(id));
export const createCampaignAsync = thunk("campaigns/create", ({ campaign }) =>
  api.createCampaign(campaign)
);
export const updateCampaignAsync = thunk("campaigns/update", ({ id, campaign }) =>
  api.updateCampaign(id, campaign)
);
export const pauseCampaignAsync = thunk("campaigns/pause", ({ id }) => api.pauseCampaign(id));
export const unpauseCampaignAsync = thunk("campaigns/unpause", ({ id }) => api.unpauseCampaign(id));
export const deleteCampaignAsync = thunk("campaigns/delete", async ({ id }) => {
  await api.deleteCampaign(id);
  return { id };
});

export const readAdvertisersAsync = thunk("advertisers/readAll", (payload = {}, { state }) =>
  api.listAdvertisers(payload, state.advertisers.metadata)
);
// Separate from the paginated Advertisers tab; refetched after it goes stale.
export const readAdvertiserOptionsAsync = thunk(
  "advertisers/readOptions",
  () => api.listAdvertiserOptions(),
  {
    condition: (_, { getState }) => {
      const { loaded, loading } = getState().ads.advertiserOptions;
      return !loaded && !loading;
    }
  }
);
export const createAdvertiserAsync = thunk("advertisers/create", ({ advertiser }) =>
  api.createAdvertiser(advertiser)
);
export const updateAdvertiserAsync = thunk("advertisers/update", ({ id, advertiser }) =>
  api.updateAdvertiser(id, advertiser)
);
// Pause or unpause: PATCH { paused }. Its campaigns read as paused while it is paused.
export const setAdvertiserPausedAsync = thunk("advertisers/setPaused", ({ id, paused }) =>
  api.updateAdvertiser(id, { paused })
);
export const deleteAdvertiserAsync = thunk("advertisers/delete", async ({ id }) => {
  await api.deleteAdvertiser(id);
  return { id };
});

// Loaded once: later dispatches are skipped while loading or after success.
export const readPlacementsAsync = thunk("placements/read", () => api.readPlacements(), {
  condition: (_, { getState }) => {
    const { loaded, loading } = getState().ads.placements;
    return !loaded && !loading;
  }
});

export const setPlacementEnabledAsync = thunk("placements/setEnabled", ({ key, enabled }) =>
  api.updatePlacement(key, { enabled })
);

export const uploadCreativeAsync = thunk("creatives/upload", ({ file, crop }, { dispatch }) =>
  api.uploadCreative(file, {
    onProgress: progress => dispatch(setUploadProgress(progress)),
    crop
  })
);
export const deleteCreativeAsync = thunk("creatives/delete", async ({ id }) => {
  await api.deleteCreative(id);
  return { id };
});

export const CREATIVE_POLL_INTERVAL_MS = 3000;
const POLL_DONE = ["ready", "failed"];
const POLL_READ_FAILED = "Could not check the video status";

const wait = (ms, signal) =>
  new Promise(resolve => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

// Tracks one creative: { id, status, durationSeconds } are what the caller already knows.
// A processing creative is read every 3 s until it is ready or failed. A ready or failed one
// is read once, at once, because the campaign read carries no duration or failure reason.
// Stops when the thunk is aborted (`promise.abort()`) or when stopCreativePoll resets the
// state, so a closing modal ends it. A failed read marks the poll failed; it writes no form
// error.
export const pollCreativeAsync = createAsyncThunk(
  "ads/creatives/poll",
  async ({ id, status }, { rejectWithValue, dispatch, getState, signal }) => {
    const active = () => !signal.aborted && getState().ads.creativePoll.creativeId === id;
    try {
      let delay = POLL_DONE.includes(status) ? 0 : CREATIVE_POLL_INTERVAL_MS;
      while (active()) {
        if (delay > 0) await wait(delay, signal);
        delay = CREATIVE_POLL_INTERVAL_MS;
        if (!active()) break;
        const creative = await api.readCreative(id);
        if (!active()) break;
        dispatch(adsSlice.actions.creativePolled(creative));
        if (POLL_DONE.includes(creative.status)) return creative;
      }
      return null;
    } catch (error) {
      return rejectWithValue(api.toRejection(error));
    }
  }
);

const initialCreativePoll = {
  creativeId: null,
  status: null,
  failureReason: null,
  durationSeconds: null
};

const initialListState = { items: [], loading: false, metadata: initialDashboardPageMetadata };

const initialState = {
  // requestId: the latest non-silent list read, which owns `loading`
  campaigns: { ...initialListState, requestId: null },
  advertisers: initialListState,
  advertiserOptions: { items: [], loaded: false, loading: false },
  placements: { items: [], targeting: null, loaded: false, loading: false },
  creativeUpload: { uploading: false, progress: 0 },
  creativePoll: initialCreativePoll,
  // { field: message } for the form that last failed, and the HTTP status of that failure
  error: null,
  errorStatus: null
};

const replaceById = (items, item) =>
  items.map(existing => (existing.id === item.id ? item : existing));
const removeById = (items, id) => items.filter(item => item.id !== id);

const applyPage = (list, page) => {
  const { items, ...metadata } = page || {};
  list.loading = false;
  list.items = items || [];
  list.metadata = mergeDshbPageMetadataObjects(list.metadata, metadata);
};

const isSilent = action => Boolean(action.meta.arg?.silent);

const staleAdvertiserOptions = state => {
  state.advertiserOptions.loaded = false;
};

const failure = (state, action) => {
  state.error = action.payload?.fields || {};
  state.errorStatus = action.payload?.status ?? null;
};

const resetError = state => {
  state.error = null;
  state.errorStatus = null;
};

// Update, pause and unpause answer with the campaign read shape; the row is replaced.
const replaceCampaign = (state, action) => {
  state.campaigns.items = replaceById(state.campaigns.items, action.payload);
};

export const adsSlice = createSlice({
  name: "ads",
  initialState,
  reducers: {
    clearError: (state, action) => {
      if (state.error) state.error[action.payload.field] = null;
    },
    clearErrors: resetError,
    setUploadProgress: (state, action) => {
      state.creativeUpload.progress = action.payload;
    },
    // A creative read by the poll: { id, status, failureReason, durationSeconds }.
    creativePolled: (state, action) => {
      const { id, status, failureReason, durationSeconds } = action.payload;
      if (state.creativePoll.creativeId !== id) return;
      state.creativePoll = {
        creativeId: id,
        status,
        failureReason: failureReason ?? null,
        durationSeconds: durationSeconds ?? null
      };
    },
    // Stops a running poll and clears its state.
    stopCreativePoll: state => {
      state.creativePoll = initialCreativePoll;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(readCampaignsAsync.pending, (state, action) => {
        if (isSilent(action)) return;
        state.campaigns.loading = true;
        state.campaigns.requestId = action.meta.requestId;
      })
      .addCase(readCampaignsAsync.fulfilled, (state, action) => {
        const { loading } = state.campaigns;
        applyPage(state.campaigns, action.payload);
        if (isSilent(action)) state.campaigns.loading = loading;
      })
      .addCase(readCampaignsAsync.rejected, (state, action) => {
        // A read replaced by a newer one leaves loading to the newer one; the latest read,
        // aborted by an unmount or a tab switch, clears it.
        if (!isSilent(action) && action.meta.requestId === state.campaigns.requestId) {
          state.campaigns.loading = false;
        }
        if (action.meta.aborted || isSilent(action)) return;
        failure(state, action);
      })
      .addCase(createCampaignAsync.pending, resetError)
      .addCase(createCampaignAsync.rejected, failure)
      .addCase(updateCampaignAsync.pending, resetError)
      .addCase(updateCampaignAsync.fulfilled, replaceCampaign)
      .addCase(updateCampaignAsync.rejected, failure)
      .addCase(readCampaignAsync.rejected, failure)
      .addCase(pauseCampaignAsync.fulfilled, replaceCampaign)
      .addCase(pauseCampaignAsync.rejected, failure)
      .addCase(unpauseCampaignAsync.fulfilled, replaceCampaign)
      .addCase(unpauseCampaignAsync.rejected, failure)
      .addCase(deleteCampaignAsync.fulfilled, (state, action) => {
        state.campaigns.items = removeById(state.campaigns.items, action.payload.id);
      })
      .addCase(deleteCampaignAsync.rejected, failure)
      .addCase(readAdvertisersAsync.pending, state => {
        state.advertisers.loading = true;
      })
      .addCase(readAdvertisersAsync.fulfilled, (state, action) => {
        applyPage(state.advertisers, action.payload);
      })
      .addCase(readAdvertisersAsync.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.advertisers.loading = false;
        failure(state, action);
      })
      .addCase(createAdvertiserAsync.pending, resetError)
      .addCase(createAdvertiserAsync.fulfilled, staleAdvertiserOptions)
      .addCase(createAdvertiserAsync.rejected, failure)
      .addCase(updateAdvertiserAsync.pending, resetError)
      .addCase(updateAdvertiserAsync.fulfilled, (state, action) => {
        state.advertisers.items = replaceById(state.advertisers.items, action.payload);
        staleAdvertiserOptions(state);
      })
      .addCase(updateAdvertiserAsync.rejected, failure)
      .addCase(setAdvertiserPausedAsync.fulfilled, (state, action) => {
        state.advertisers.items = replaceById(state.advertisers.items, action.payload);
      })
      .addCase(deleteAdvertiserAsync.fulfilled, (state, action) => {
        state.advertisers.items = removeById(state.advertisers.items, action.payload.id);
        staleAdvertiserOptions(state);
      })
      .addCase(readAdvertiserOptionsAsync.pending, state => {
        state.advertiserOptions.loading = true;
      })
      .addCase(readAdvertiserOptionsAsync.fulfilled, (state, action) => {
        state.advertiserOptions = { items: action.payload, loaded: true, loading: false };
      })
      .addCase(readAdvertiserOptionsAsync.rejected, (state, action) => {
        state.advertiserOptions.loading = false;
        failure(state, action);
      })
      .addCase(deleteAdvertiserAsync.rejected, failure)
      .addCase(readPlacementsAsync.pending, state => {
        state.placements.loading = true;
      })
      .addCase(readPlacementsAsync.fulfilled, (state, action) => {
        const { items, targeting } = action.payload;
        state.placements = { items, targeting, loaded: true, loading: false };
      })
      .addCase(readPlacementsAsync.rejected, (state, action) => {
        state.placements.loading = false;
        failure(state, action);
      })
      .addCase(setPlacementEnabledAsync.fulfilled, (state, action) => {
        const updated = action.payload;
        state.placements.items = state.placements.items.map(placement =>
          placement.key === updated.key ? { ...placement, ...updated } : placement
        );
      })
      .addCase(pollCreativeAsync.pending, (state, action) => {
        const { id, status, durationSeconds } = action.meta.arg;
        state.creativePoll = {
          ...initialCreativePoll,
          creativeId: id,
          status: status ?? "processing",
          durationSeconds: durationSeconds ?? null
        };
      })
      .addCase(pollCreativeAsync.rejected, (state, action) => {
        if (action.meta.aborted) return;
        const { id, status } = action.meta.arg;
        // a creative already known to be ready or failed keeps that status when the read fails
        if (state.creativePoll.creativeId !== id || POLL_DONE.includes(status)) return;
        state.creativePoll.status = "failed";
        state.creativePoll.failureReason = POLL_READ_FAILED;
      })
      .addCase(uploadCreativeAsync.pending, state => {
        state.creativeUpload = { uploading: true, progress: 0 };
        resetError(state);
      })
      .addCase(uploadCreativeAsync.fulfilled, state => {
        state.creativeUpload = { uploading: false, progress: 100 };
      })
      .addCase(uploadCreativeAsync.rejected, (state, action) => {
        state.creativeUpload = { uploading: false, progress: 0 };
        failure(state, action);
      });
    // deleteCreativeAsync has no reducer case: its failures are ignored by design (CM-08).
  }
});

export const { clearError, clearErrors, setUploadProgress, stopCreativePoll } = adsSlice.actions;

export const selectCampaigns = state => state.ads.campaigns;
export const selectAdvertisers = state => state.ads.advertisers;
export const selectAdvertiserOptions = state => state.ads.advertiserOptions;
export const selectPlacements = state => state.ads.placements;
export const selectCreativeUpload = state => state.ads.creativeUpload;
export const selectCreativePoll = state => state.ads.creativePoll;
export const selectAdsError = state => state.ads.error;
export const selectAdsErrorStatus = state => state.ads.errorStatus;

export default adsSlice.reducer;
